// Los tres agentes del corredor. Solo datos del catálogo; nunca recomiendan.
// Cifras vía el mismo resolver de tokens (ND si no resuelve).
import catalogo from '../datos/catalogo.json';
import { createResolver, fmtCOP } from './resolve';

const R = createResolver(catalogo);
export const CATALOGO_TOTAL = catalogo.productos.length;

// Punto 24a: etiqueta de origen de prima legible.
const ORIGEN_LABEL = { 'tarifa publicada': 'Tarifa publicada', 'rango del ramo': 'Rango típico del mercado' };

// Mapea la necesidad/momento del lead a categorías del catálogo.
const NECESIDAD_CAT = [
  [/carro|veh[ií]culo|auto|choqu|estren/i, 'Automóvil'],
  [/tembl|sismo|terremoto|apart|copropiedad|edificio|zonas comunes/i, 'Copropiedades'],
  [/casa|hogar|mud|vivienda|remodel|arriendo/i, 'Hogar'],
  [/hij|beb|familia|falt|vida|deud/i, 'Vida'],
  [/estudi|educa|universidad|colegio/i, 'Educación'],
  [/salud|prepagada|cl[ií]nica|hospital|enferm/i, 'Salud'],
  [/celular|port[aá]til|bici|patineta|dispositivo|movilidad/i, 'Dispositivos y movilidad'],
  [/tarjeta|asistencia/i, 'Asistencias de tarjeta'],
];

function categoriasDe(lead) {
  const cats = new Set((lead.productos || []).filter((c) => catalogo.productos.some((p) => p.categoria === c)));
  if (!cats.size) {
    const txt = `${lead.necesidad || ''} ${(lead.productos || []).join(' ')}`;
    for (const [re, cat] of NECESIDAD_CAT) if (re.test(txt)) cats.add(cat);
  }
  if (cats.has('Hogar')) cats.delete('Copropiedades'); // punto 24d: no mezclar Copropiedades si pidió Hogar
  if (!cats.size) cats.add('Vida');
  return [...cats];
}

// Extrae la edad máxima de ingreso de reglas_negocio si aparece; '' si no hay dato.
function edadMax(p) {
  const rn = String(p.reglas_negocio || '');
  const m = /(\d{2})\s*a[nñ]os/i.exec(rn) || /ingreso[^.]*?(\d{2})/i.exec(rn);
  return m ? `${m[1]} años` : '';
}

const corto = (s, n = 90) => {
  const t = String(s || '').split(';')[0].trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

// Punto 24b: dato faltante → "Por confirmar" (en vez de ND).
const pc = (v) => (v && v !== 'ND' ? v : 'Por confirmar');

// Agente 1 · catálogo: filtra por la necesidad del lead.
export function agenteCatalogo(lead) {
  const cats = categoriasDe(lead);
  const prods = catalogo.productos.filter((p) => cats.includes(p.categoria));
  // Prioriza los de mercado y deja como máximo 4 para la tabla.
  const orden = prods.sort((a, b) => (a.prioridad === 'MERCADO' ? -1 : 0) - (b.prioridad === 'MERCADO' ? -1 : 0));
  return { categorias: cats, n: prods.length, total: CATALOGO_TOTAL, productos: orden.slice(0, 4) };
}

// Agente 2 · comparador: tabla lado a lado, solo con datos del catálogo.
export function agenteComparador(prods) {
  return prods.map((p) => {
    const em = edadMax(p);
    // Punto 24b: en Automóvil/Hogar la edad no aplica; en el resto, "Por confirmar".
    const edad = em || (['Automóvil', 'Hogar'].includes(p.categoria) ? 'No aplica' : 'Por confirmar');
    return {
      id: p.id,
      nombre: p.producto,
      categoria: p.categoria,
      prima: p.prima_min != null
        ? (p.prima_min === p.prima_max ? `${fmtCOP(p.prima_min)}/mes` : `${fmtCOP(p.prima_min)}–${fmtCOP(p.prima_max)}/mes`)
        : 'Por confirmar',
      origen: ORIGEN_LABEL[p.prima_origen] || p.prima_origen || 'Por confirmar',
      deducible: pc(R.ded(p.id)),
      cubre: pc(corto(p.coberturas_clave, 110)),
      excluye: pc(corto(p.exclusiones_principales, 90)),
      edad,
      edadReal: !!em,
    };
  });
}

// Agente 3 · cumplimiento: alertas y verificación de que nada es recomendación.
export function agenteCumplimiento(lead, prods) {
  const alertas = [];
  if (prods.some((p) => edadMax(p))) {
    alertas.push({ tipo: 'Edad de ingreso', txt: 'Al menos un plan tiene edad máxima de ingreso: el asesor la valida con el cliente. El asistente no pregunta la edad.' });
  }
  // Punto 25: alerta específica para Automóvil (carro financiado · banco beneficiario).
  if (prods.some((p) => p.categoria === 'Automóvil')) {
    alertas.push({ tipo: 'Automóvil financiado', txt: 'Si el carro es financiado, el banco va como beneficiario. El cliente puede traer su propia póliza endosada.' });
  }
  if (prods.some((p) => /deudor|cr[eé]dito|hipotec/i.test(`${p.producto} ${p.riesgo_principal}`))
      || prods.some((p) => p.id === 'MKT-019')
      || prods.some((p) => p.categoria === 'Copropiedades')) {
    alertas.push({ tipo: 'Póliza atada a crédito', txt: 'Puede haber pólizas de deudores atadas a un crédito: tomador y beneficiario es la entidad; asegurado, el cliente. Revisar duplicidad con lo que ya paga.' });
  }
  if (prods.some((p) => p.categoria === 'Salud' && /directa/i.test(p.contratacion || ''))
      || prods.some((p) => ['MKT-021', 'MKT-022', 'MKT-023', 'MKT-024', 'MKT-025', 'MKT-029'].includes(p.id))) {
    alertas.push({ tipo: 'Contratación directa (salud)', txt: 'Salud complementaria/prepagada la contrata directo la persona: no viene incluida en un crédito ni la pone la entidad.' });
  }
  if (!lead.whatsapp) alertas.push({ tipo: 'Documentos faltantes', txt: 'Falta el WhatsApp de contacto para agendar la llamada.' });
  if (!lead.consent) alertas.push({ tipo: 'Documentos faltantes', txt: 'Falta el consentimiento de tratamiento de datos.' });
  if (!alertas.length) alertas.push({ tipo: 'Sin alertas', txt: 'No se detectaron banderas en este lead.' });
  return alertas;
}

// Contexto enriquecido para la voz: producto, prima, deducible (del catálogo) y qué falta (por caso).
export function contextoVoz(lead) {
  const { productos } = agenteCatalogo(lead);
  const p = productos[0] || {};
  const txt = `${lead.necesidad || ''} ${(lead.productos || []).join(' ')}`.toLowerCase();
  let falta = 'revisar contigo deducible y coberturas';
  if (/carro|veh|auto|choqu|estren/.test(txt)) falta = 'confirmar que la póliza esté vigente desde la entrega del carro';
  else if (/tembl|sismo|terremoto|apart|copropiedad|hogar|casa|mud/.test(txt)) falta = 'confirmar el valor de reconstrucción y sumar la cobertura de contenidos (muebles)';
  else if (/hij|beb|familia|vida|falt/.test(txt)) falta = 'definir beneficiarios y el monto que protege a tu familia';
  else if (/trabajo|renunci|empleo|independ/.test(txt)) falta = 'retomar las coberturas que perdiste al cambiar de empleo';
  return {
    producto: p.producto || (lead.productos || [])[0] || 'tus seguros',
    prima: p.id ? R.prima(p.id) : 'ND',
    deducible: p.id ? R.ded(p.id) : 'ND',
    falta,
  };
}

export function fichaLlamada(lead, prods) {
  const cats = [...new Set(prods.map((p) => p.categoria))].join(', ');
  return {
    resumen: [
      `${lead.nombre || 'Cliente'} · ${lead.necesidad || 'necesidad por definir'} · WhatsApp ${lead.whatsapp || 'ND'}`,
      `Interés: ${cats || 'ND'}. ${prods.length} opciones del catálogo comparadas.`,
      'La decisión y la recomendación son del corredor humano; el asistente solo mostró datos.',
    ],
    entendido: lead.entendido || [],
    preguntas: [
      '¿Qué es lo que más te preocupa proteger hoy?',
      '¿Ya pagas alguna cobertura por tu tarjeta, empleador o crédito?',
      '¿Prefieres que revisemos primero deducible o cobertura?',
    ],
  };
}
